import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    initialCountry: String,
    initialState: String,
  };

  static targets = [
    "stateLabel",
    "stateDropdown",
    "stateTextbox",
    "stateField",
    "cityField",
    "countryDropdown",
    "zipField",
    "addressPlaceholder",
    "address2Placeholder"
  ];

  initialize() {
    this.stateFieldTarget.value = this.initialStateValue;
    this.stateMap = {};
    this.initializeCountriesAndStateInputs();
  }

  /*
    Fetches countries and their states, and creates state map.
    Based on selected country by the user, it renders state dropdown
    if country is from SUPPORTED_COUNTRY_LIST_WITH_STATES else it renders input textbox.

    Note: Countries like UK, Ireland, Canada etc. have provinces, subdivision, counties and not "state".
    Countries like USA and India have "states". Thus, to manage this dynamically and
    still save state information in same field, we are toggling state display input fields.
  */
  async initializeCountriesAndStateInputs() {
    const countriesAndStates = await this.fetchCountriesAndStates();
    this.generateCountryToStateMap(countriesAndStates);
    this.displayStateInputs();
    this.updateStateLabelText();
    this.updateAddressPlaceholderText();
    this.updateZipLabelText();
    this.handleCountrySummary();
  }

  async fetchCountriesAndStates() {
    const response = await fetch("/accounts/countries");
    return await response.json();
  }


  /*
    Countries from SUPPORTED_COUNTRY_LIST_WITH_STATES need to show choices for States/Provinces/Counties, while others do not.
    Therefore, we render a dropdown if the country belongs to SUPPORTED_COUNTRY_LIST_WITH_STATES; otherwise, we render an input field.
    To manage this dynamically and still save state information in the same model field,
    we are toggling the state display input fields in this method.
  */
  displayStateInputs() {
    this.countryDropdownTarget.value = this.initialCountryValue;
    if (this.canCountryShowStates()) {
      this.populateStateDropdown();
      this.showStateDropdown();
    } else {
      if (this.initialStateValue) {
        this.stateTextboxTarget.value = this.initialStateValue;
      }
      this.showStateTextInput();
    }
  }

  generateCountryToStateMap(data) {
    data.forEach((country) => {
      if (country.states.length > 0) {
        this.stateMap[country.alpha2] = country.states.sort((a, b) =>
          a.name > b.name ? 1 : -1
        );
      } else {
        this.stateMap[country.alpha2] = [];
      }
    });
  }

  /*
    Recreates state list of the selected country with Blank Option.
    Also, assigns selected attributes if the initial state is present
  */
  populateStateDropdown() {
    Array.from(this.stateDropdownTarget.children).forEach((c) => c.remove());
    const fragment = document.createDocumentFragment();
    const blankOption = document.createElement("option");
    blankOption.value = "";
    blankOption.text = "Select";
    fragment.appendChild(blankOption);

    this.stateMap[this.countryDropdownTarget.value].forEach((state) => {
      const newOption = document.createElement("option");
      newOption.value = state.code;
      newOption.text = state.name;
      if (this.stateFieldTarget.value === state.code) {
        newOption.selected = "selected";
      }
      fragment.appendChild(newOption);
    });
    this.stateDropdownTarget.appendChild(fragment);
  }

  canCountryShowStates() {
    return this.stateMap[this.countryDropdownTarget.value].length;
  }

  /*
    Repopulates States For SUPPORTED_COUNTRY_LIST_WITH_STATES.
    For the remaining Countries, it resets State value in the textbox.
  */
  handleCountryChange() {
    if (this.canCountryShowStates()) {
      this.populateStateDropdown();
      this.showStateDropdown();
      this.handleStateDropdownChange();
    } else {
      this.showStateTextInput();
      this.stateTextboxTarget.value = "";
      this.handleStateTextChange();
    }
    this.updateStateLabelText();
    this.updateAddressPlaceholderText();
    this.updateZipLabelText();
  }

  handleStateDropdownChange() {
    this.stateFieldTarget.value = this.stateDropdownTarget.value;
    this.stateFieldTarget.dataset.summaryDisplayValue =
      this.stateDropdownTarget.selectedOptions[0].value !== ""
        ? this.stateDropdownTarget.selectedOptions[0].text
        : "";
    this.handleCountrySummary();
  }

  handleStateTextChange() {
    this.stateFieldTarget.value = this.stateTextboxTarget.value;
    this.stateFieldTarget.dataset.summaryDisplayValue =
      this.stateTextboxTarget.value;
    this.handleCountrySummary();
  }

  handleCityTextChange() {
    this.handleCountrySummary();
  }

  /* Removes Country omission from address summary if no state/city present */
  handleCountrySummary() {
    if (this.stateFieldTarget.value || this.cityFieldTarget.value) {
      this.countryDropdownTarget.removeAttribute("data-omit-from-summary");
    } else {
      this.countryDropdownTarget.setAttribute("data-omit-from-summary", "true");
    }
  }

   /*
    State is referred to as:
      "Province" for Canada
      "County" in the United Kingdom of Great Britain and Northern Ireland.
      "State" in the US, India, Australia, and other countries.
  */
  updateStateLabelText() {
    let stateLabel = "";
    let placeholderVal = "State/Province";
    switch (this.countryDropdownTarget.value) {
      case "CA":
        stateLabel = "PROVINCE";
        break;
      case "GB":
      case "IE":
        stateLabel = "COUNTY";
        placeholderVal = "County";
        break;
      case "NZ":
        stateLabel = "REGION";
        break;
      default:
        stateLabel = "STATE";
    }
    this.stateLabelTarget.innerText = stateLabel;
    this.stateTextboxTarget.placeholder = placeholderVal;
  }

  updateAddressPlaceholderText() {
    let addressPlaceholderVal = "Street address";
    let address2PlaceholderVal = "Apt., suite, unit number, etc (optional)";
    let cityPlaceholderVal = "";

    switch(this.countryDropdownTarget.value) {
      case "AU":
        addressPlaceholderVal = "Property or Building Name";
        address2PlaceholderVal = "Level, shop, unit / Street address (optional)";
        cityPlaceholderVal = "Suburb";
        break;
      case "GB":
      case "IE":
        addressPlaceholderVal = "Property Name";
        address2PlaceholderVal = "Level, shop, unit / Street address (optional)";
        break;
    }

    this.addressPlaceholderTarget.placeholder = addressPlaceholderVal;
    this.address2PlaceholderTarget.placeholder = address2PlaceholderVal;
    this.cityFieldTarget.placeholder = cityPlaceholderVal;
  }

  /*
    Postal code is referred to with different names. Example:
      "Zip" for US
      "Eircode" for Ireland.
      "Pincode" in the India.
    To generalize, we'd be using universal term "POSTAL CODE" for all countries
    and "ZIP" for US
  */
  updateZipLabelText() {
    let zipLabel = "";
    switch (this.countryDropdownTarget.value) {
      case "US":
        zipLabel = "ZIP";
        break;
      default:
        zipLabel = "POSTAL CODE";
    }
    this.zipFieldTarget.labels[0].innerText = zipLabel;
  }

  showStateDropdown() {
    this.stateTextboxTarget.classList.add("hidden");
    this.stateDropdownTarget.classList.remove("hidden");
  }

  showStateTextInput() {
    this.stateTextboxTarget.classList.remove("hidden");
    this.stateDropdownTarget.classList.add("hidden");
  }
}
